import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// API URL 설정
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// 프로젝트 수정 페이지
function EditProject() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    title: '',
    project_type: 'ongoing',
    period: '',
    agency: '',
    description: '',
    display_order: 0
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // 기존 데이터 로드
  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/projects/${id}`);
      const data = await response.json();

      if (data.success) {
        setFormData({
          title: data.data.title || '',
          project_type: data.data.project_type || 'ongoing',
          period: data.data.period || '',
          agency: data.data.agency || '',
          description: data.data.description || '',
          display_order: data.data.display_order || 0
        });
      } else {
        setError('Failed to load project data');
      }
    } catch (err) {
      console.error('Error fetching project:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  // 입력값 변경 처리
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 폼 제출 처리
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title) {
      setError('Title is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const response = await fetch(`${API_URL}/projects/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/projects');
        }, 1500);
      } else {
        setError(data.error || 'Failed to update project');
      }
    } catch (err) {
      console.error('Error updating project:', err);
      setError('Failed to connect to server');
    } finally {
      setSaving(false);
    }
  };

  // 삭제 처리
  const handleDelete = async () => {
    if (!window.confirm('정말로 이 프로젝트를 삭제하시겠습니까?\n\n삭제된 데이터는 복구할 수 없습니다.')) {
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/projects/${id}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (data.success) {
        alert('삭제되었습니다.');
        navigate('/projects');
      } else {
        setError(data.error || 'Failed to delete project');
      }
    } catch (err) {
      console.error('Error deleting project:', err);
      setError('Failed to connect to server');
    } finally {
      setSaving(false);
    }
  };

  // 로딩 중
  if (loading) {
    return (
      <div className="site-content" style={{ textAlign: 'center', padding: '50px' }}>
        <p>Loading project data...</p>
      </div>
    );
  }

  // 성공 메시지
  if (success) {
    return (
      <div className="site-content" style={{ textAlign: 'center', padding: '50px' }}>
        <h2 style={{ color: 'green' }}>Projects 페이지가 성공적으로 갱신되었습니다.</h2>
        <p>Projects 페이지로 돌아가는 중...</p>
      </div>
    );
  }

  return (
    <div className="site-content">
      <h2 className="section-title">Edit Project</h2>

      {error && (
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          borderRadius: '5px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Title */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            프로젝트 제목 <span style={{ color: 'red' }}>*</span>
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          />
        </div>

        {/* Project Type */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            프로젝트 진행 <span style={{ color: 'red' }}>*</span>
          </label>
          <select
            name="project_type"
            value={formData.project_type}
            onChange={handleChange}
            required
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          >
            <option value="ongoing">Ongoing</option>
            <option value="completed">Completed</option>
          </select>
        </div>

        {/* Period */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            기간
          </label>
          <input
            type="text"
            name="period"
            value={formData.period}
            onChange={handleChange}
            placeholder="{YYYY}.{MM}. ~ {YYYY}.{MM}."
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          />
        </div>

        {/* Agency */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            기관
          </label>
          <input
            type="text"
            name="agency"
            value={formData.agency}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          />
        </div>

        {/* Description */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            설명
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows="4"
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              resize: 'vertical'
            }}
          />
        </div>

        {/* Display Order */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            표시 순서
          </label>
          <small style={{ color: '#666' }}>낮은 숫자가 먼저 표시됩니다. (0: 가장 높은 우선순위)</small>
          <input
            type="number"
            name="display_order"
            value={formData.display_order}
            onChange={handleChange}
            min="0"
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          />
        </div>

        {/* Delete Button */}
        <div style={{ marginBottom: '30px' }}>
          <button
            type="button"
            onClick={handleDelete}
            disabled={saving}
            style={{
              width: '100%',
              padding: '14px',
              fontSize: '16px',
              fontWeight: 'bold',
              backgroundColor: '#dc3545',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1
            }}
          >
            삭제
          </button>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button
            type="button"
            onClick={() => navigate('/projects')}
            style={{
              padding: '12px 30px',
              fontSize: '16px',
              backgroundColor: '#ddd',
              color: '#333',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            취소
          </button>
          <button
            type="submit"
            disabled={saving}
            style={{
              padding: '12px 30px',
              fontSize: '16px',
              backgroundColor: '#0066cc',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1
            }}
          >
            {saving ? 'Updating...' : '변경 사항 저장'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditProject;
